import useReviewBulkPointModifier from '@/containers/ReviewBulkPointModifiers/ReviewBulkPointModifier-hooks';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {useActionDropDown} from '@/hooks/useActionDropDown';
import {
  downloadTemplateFile,
  TEMPLATE_TYPE
} from '@/services/downloadTemplateFile';
import {CreateBulkModifiersSchemas} from '@/services/schema/BulkPointModifiers/CreateBulkModifiersJSONSchemas';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetBulkModifiers,
  setBulkModifiers
} from '@/store/slices/bulkPointSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {setStoreListUrl} from '@/store/slices/salesTargetSlice';
import {
  ACTION_DROPDOWN,
  EXCEL_ACTION_OPTION,
  SCHEDULE_DROPDOWN,
  STORE_SELECTOR_DROPDOWN
} from '@/utils/constants/commonConstants';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

export const useCreateBulkPointModifiers = () => {
  const dispatch = useAppDispatch();
  const bulkModifier = useAppSelector((state) => state?.bulkModifiers);
  const [formData, setFormData] = useState(bulkModifier);
  const [isNameValid, setIsNameValid] = useState(true);

  const {dropdownList: dropdownListStoreSelector} = useDropdown({
    dropdownType: DROPDOWN_TYPES.StoreSelector
  });
  const {dropdownList: dropdownListActions} = useActionDropDown();
  const {dropdownList: dropdownListSchedule} = useDropdown({
    dropdownType: DROPDOWN_TYPES.Schedule
  });

  const [storeSelectorList, setStoreSelectorList] = useState({});
  const [actionsList, setActionsList] = useState({});
  const [scheduleList, setScheduleList] = useState({});

  const {REMOVE_ALL_EXCEPT, REMOVE_NUM_POINTS, ADD_POINTS} =
    ACTION_DROPDOWN || {};
  const {END_OF_MONTH, END_OF_YEAR, CUSTOM_SCHEDULE} = SCHEDULE_DROPDOWN || {};
  const {EXCEL_UPLOAD} = STORE_SELECTOR_DROPDOWN || {};
  const {handleDraft, handleNameValidation} = useReviewBulkPointModifier();
  const navigate = useNavigate();
  const [isCancel, setIsCancel] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);

  const {
    bulkModifierSetupJsonSchema,
    bulkModifierSetupUISchema,
    rulesJsonSchema,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema,
    scheduleUISchema,
    startDateJsonSchema,
    startDateUISchema,
    endDateJsonSchema,
    endDateUISchema
  } = CreateBulkModifiersSchemas;

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => {
      // Check if storeSelector is being changed
      if (
        Object.hasOwn(data, 'storeSelector') &&
        data.storeSelector !== prev.storeSelector
      ) {
        return {
          ...prev,
          ...data,
          actions: ''
        };
      }
      return {
        ...prev,
        ...data
      };
    });
  }, []);

  const handleFormReset = () => {
    dispatch(resetBulkModifiers());
  };

  const handleSetStoreSelectorList = useCallback(
    (bulkModifierSetupJsonSchema, dropdownListStoreSelector) => {
      if (!dropdownListStoreSelector) return;
      if (
        bulkModifierSetupJsonSchema &&
        dropdownListStoreSelector?.length > 0
      ) {
        let updatedDropdownData = structuredClone(bulkModifierSetupJsonSchema);
        if (updatedDropdownData?.properties?.storeSelector) {
          updatedDropdownData.properties.storeSelector.options =
            dropdownListStoreSelector.filter((opt) => opt?.displayText);
        }
        setStoreSelectorList(updatedDropdownData);
      }
    },
    []
  );

  // Helper function to get Excel-specific options
  const getExcelOptions = useCallback((dropdownListActions) => {
    const excelOption = dropdownListActions.find(
      (opt) =>
        (opt.displayText && opt.displayText === EXCEL_ACTION_OPTION) ||
        (opt.value && opt.value === EXCEL_ACTION_OPTION)
    );
    return excelOption ? [excelOption] : [];
  }, []);

  // Helper function to filter out Excel-specific options
  const getFilteredOptions = useCallback((dropdownListActions) => {
    return dropdownListActions.filter(
      (opt) =>
        !(
          (opt.displayText && opt.displayText === EXCEL_ACTION_OPTION) ||
          (opt.value && opt.value === EXCEL_ACTION_OPTION)
        )
    );
  }, []);

  // Helper function to get updated options based on storeSelector
  const getUpdatedOptions = useCallback(
    (dropdownListActions, storeSelector) => {
      if (storeSelector && storeSelector.toLowerCase() === 'excel upload') {
        return getExcelOptions(dropdownListActions);
      }
      return getFilteredOptions(dropdownListActions);
    },
    [getFilteredOptions, getExcelOptions]
  );

  const handleSetActionsList = useCallback(
    (rulesJsonSchema, dropdownListActions, storeSelector) => {
      if (!dropdownListActions) return;

      if (
        typeof rulesJsonSchema === 'object' &&
        dropdownListActions?.length > 0
      ) {
        const updatedDropdownData = structuredClone(rulesJsonSchema);

        if (updatedDropdownData?.properties?.actions) {
          updatedDropdownData.properties.actions.options = getUpdatedOptions(
            dropdownListActions,
            storeSelector
          );
        }

        setActionsList(updatedDropdownData);
      }
    },
    [getUpdatedOptions]
  );

  const handleSetScheduleList = useCallback(
    (scheduleJsonSchema, dropdownListSchedule) => {
      if (!dropdownListSchedule) return;
      if (
        typeof scheduleJsonSchema === 'object' &&
        dropdownListSchedule?.length > 0
      ) {
        let updatedDropdownData = structuredClone(scheduleJsonSchema);
        if (updatedDropdownData?.properties?.schedule) {
          updatedDropdownData.properties.schedule.options = [
            ...dropdownListSchedule
          ];
        }
        setScheduleList(updatedDropdownData);
      }
    },
    []
  );

  const validateFormData = useCallback(
    ({
      bulkModifierName,
      bulkModifierNameValid,
      storeSelector,
      actions,
      numberOfDays,
      numberOfDaysValid,
      numberOfPoints,
      numberOfPointsValid,
      schedule,
      startDate,
      startDateValid,
      storeList
    }) => {
      const actionsVal = actions?.toLowerCase();
      const scheduleVal = schedule?.toLowerCase();
      const isExcelUpload =
        storeSelector && storeSelector.toLowerCase() === EXCEL_UPLOAD;

      const isInValid = [
        !bulkModifierName || bulkModifierName === '' || bulkModifierNameValid,
        !storeSelector || storeSelector === '',
        !actionsVal ||
          actionsVal === '' ||
          (actionsVal === REMOVE_ALL_EXCEPT &&
            (numberOfDays === '' || numberOfDaysValid)) ||
          ((actionsVal === REMOVE_NUM_POINTS || actionsVal === ADD_POINTS) &&
            (numberOfPoints === '' || numberOfPointsValid)),
        !scheduleVal || scheduleVal === '',
        (scheduleVal === CUSTOM_SCHEDULE &&
          (startDate === '' || startDateValid)) ||
          (isExcelUpload && (!storeList || storeList === ''))
      ].some(Boolean);

      return isInValid;
    },
    [
      ADD_POINTS,
      CUSTOM_SCHEDULE,
      EXCEL_UPLOAD,
      REMOVE_ALL_EXCEPT,
      REMOVE_NUM_POINTS
    ]
  );

  const getScheduleInfo = useCallback(
    (scheduleVal) => {
      const scheduleOptions = {
        [CUSTOM_SCHEDULE]: {
          scheduleRowClass: 'w-1/2',
          showStartDate: true,
          showEndDate: false
        },
        [END_OF_MONTH]: {
          scheduleRowClass: 'w-1/2',
          showStartDate: false,
          showEndDate: true
        },
        [END_OF_YEAR]: {
          scheduleRowClass: 'w-1/2',
          showStartDate: false,
          showEndDate: true
        }
      };

      return (
        scheduleOptions[scheduleVal] || {
          scheduleRowClass: 'w-1/4',
          showStartDate: false,
          showEndDate: false
        }
      );
    },
    [CUSTOM_SCHEDULE, END_OF_MONTH, END_OF_YEAR]
  );

  const handleFileDownload = async () => {
    const body = {type: TEMPLATE_TYPE.BULK_POINT_MODIFIER};
    const response = await downloadTemplateFile(body);
    if (!response) return;
    const fileName = `BulkPointModifierTemplate_${getDateWithTimeString()}`;
    downloadCSVFiles(response, fileName);
  };

  const handleValidationModalClose = () => {
    setIsNameValid(true);
  };

  useEffect(() => {
    // append  data to storeSelector dropdown
    handleSetStoreSelectorList(
      bulkModifierSetupJsonSchema,
      dropdownListStoreSelector
    );

    // append data to actions dropdown, filtered for Excel Upload
    handleSetActionsList(
      rulesJsonSchema,
      dropdownListActions,
      formData?.storeSelector
    );

    // append data to schedule dropdown
    handleSetScheduleList(scheduleJsonSchema, dropdownListSchedule);
  }, [
    handleSetStoreSelectorList,
    handleSetActionsList,
    handleSetScheduleList,
    bulkModifierSetupJsonSchema,
    dropdownListStoreSelector,
    rulesJsonSchema,
    dropdownListActions,
    formData?.storeSelector,
    scheduleJsonSchema,
    dropdownListSchedule
  ]);

  const validateName = useCallback(async () => {
    const bulkModifierName = formData?.bulkModifierName;

    // Check if name is empty or whitespace only
    if (!bulkModifierName || String(bulkModifierName).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Bulk Modifier Name is mandatory',
          type: 'error'
        })
      );
      return false;
    }

    const params = {
      name: bulkModifierName.trim(),
      id: formData?.id ?? null,
      uuid: formData?.uuid ?? null
    };
    const resp = await handleNameValidation(params);
    const isValid = resp?.success === true;
    setIsNameValid(isValid);
    return isValid;
  }, [formData, dispatch, handleNameValidation, setIsNameValid]);

  const handleSaveDraft = useCallback(async () => {
    const data = await validateName();
    if (data) {
      handleDraft();
    }
  }, [validateName, handleDraft]);

  const handleNext = useCallback(async () => {
    const isValidName = await validateName();
    if (!isValidName) return;
    dispatch(setBulkModifiers(formData));
    navigate(ROUTES.BULK_POINT_MODIFIER_REVIEW);
  }, [validateName, dispatch, formData, navigate]);

  const handleCancel = useCallback(() => {
    setIsCancel(!isCancel);
  }, [isCancel]);

  const onClickPrimaryButton = useCallback(() => {
    navigate(ROUTES.BULK_POINT_MODIFIERS);
  }, [navigate]);

  const onClickSecondaryButton = useCallback(() => {
    setIsCancel(!isCancel);
  }, [isCancel]);

  const handleScheduleChange = useCallback(
    (data) => {
      data.startDate = '';
      data.endDate = '';
      handleFormChange(data);
    },
    [handleFormChange]
  );

  const handleFileDelete = useCallback(() => {
    setFormData((prev) => ({...prev, targetMediaFile: []}));
    dispatch(setStoreListUrl(''));
    setOpenUpload(true);
  }, [setFormData, dispatch, setOpenUpload]);

  useEffect(() => {
    dispatch(setBulkModifiers(formData));
  }, [dispatch, formData]);

  return {
    formData,
    setFormData,
    handleFormChange,
    handleFileDownload,
    handleFormReset,
    validateFormData,
    getScheduleInfo,
    isNameValid,
    setIsNameValid,
    bulkModifierSetupJsonSchema: storeSelectorList,
    bulkModifierSetupUISchema,
    rulesJsonSchema: actionsList,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema: scheduleList,
    scheduleUISchema,
    startDateJsonSchema,
    startDateUISchema,
    endDateJsonSchema,
    endDateUISchema,
    handleValidationModalClose,
    handleSaveDraft,
    handleNext,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    handleScheduleChange,
    handleFileDelete,
    openUpload,
    setOpenUpload,
    isCancel,
    navigate
  };
};
